/**
 * Toolbar control family — the reusable buttons every editor composes
 * into EditorToolbar actions/status slots.
 *
 * Design contract (see the ws-tool-* CSS): ModeToggle stays the singular
 * hero control — a carved well with a solid raised chip. Everything here
 * is deliberately quieter, and toggles use the INVERSE active metaphor:
 * they latch by pressing INTO the glass (recessed + accent tint), so an
 * active tool reads differently from the active mode at a glance.
 *
 *   ToolButton  — labeled action ("Logout", "Export"); variants:
 *                 default (quiet chip) / accent (solid indigo) / danger
 *   IconButton  — quiet square icon action (back, refresh, fullscreen)
 *   ToolToggle  — labeled on/off latch ("Filter", "WHERE", "Auto")
 *   IconToggle  — icon-only on/off latch
 *
 * Icon-only controls show `label` through `data-tooltip` (TooltipHost in
 * @papan/core-ui), not the native `title` — see TooltipHost for why.
 *
 * A labeled control WITH an icon wraps its label in `.ws-tb-label`, so a compact
 * EditorToolbar (`compactLabels`) can take it icon-only before folding it away.
 */
import { useContext, type ReactNode, type ButtonHTMLAttributes } from 'react'
import { hintAttrs } from '@papan/core-ui/ui/tooltipHint'
import { ToolbarCompactContext } from './toolbarCompact'

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement>

/** A label as plain text, when it has an honest one.
 *
 *  `<ToolButton icon={…}>Run {n}</ToolButton>` arrives as an ARRAY of strings
 *  and numbers, not a string — and the compact bar hides that label exactly
 *  like a plain one, so keying the hint on `typeof children === 'string'` left
 *  those controls as icons with no hint at all. Anything richer (an element)
 *  has no text form to borrow; the caller must pass `data-tooltip`. */
export function labelText(children: ReactNode): string | undefined {
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  if (Array.isArray(children) && children.length > 0
      && children.every(c => typeof c === 'string' || typeof c === 'number')) {
    return children.join('')
  }
  return undefined
}

/** While the toolbar is icon-only, the label that just got hidden becomes the
 *  hover hint — unless the caller already gave the control one of its own. */
function useCompactHint(icon: ReactNode, children: ReactNode, rest: object): string | undefined {
  const compact = useContext(ToolbarCompactContext)
  // `in` would count an explicitly-undefined prop as "the caller gave it one",
  // leaving the control with a hidden label and no hint at all.
  if (!compact || !icon || (rest as { 'data-tooltip'?: unknown })['data-tooltip'] !== undefined) return undefined
  const text = labelText(children)
  if (text === undefined && children != null && children !== false && import.meta.env.DEV) {
    // The label is hidden and nothing replaced it: an unlabelled square.
    console.error(
      '[ToolbarControls] this control\'s label is not plain text, so it has no hint once the ' +
      'toolbar goes icon-only. Pass an explicit data-tooltip.',
    )
  }
  return text
}

/** Which hint a LABELLED control offers, and which one it offers once disabled.
 *
 *  `shown` is what a hoverable control puts on `data-tooltip`: the caller's own
 *  hint, else the label the compact bar just hid — nothing while the label is
 *  on screen to read.
 *
 *  `disabled` widens that to the label text, because a disabled control shows
 *  no hint at all otherwise (it dispatches no pointer events, so TooltipHost
 *  never sees the hover) and an icon whose label is hidden would be a mystery
 *  square. `hintAttrs` routes it to the native `title`, which a disabled
 *  control still renders. */
function hintsFor(own: string | undefined, compactHint: string | undefined, children: ReactNode) {
  const shown = own ?? compactHint
  return {
    shown,
    disabled: shown ?? labelText(children),
  }
}

export function ToolButton({
  icon,
  variant = 'default',
  className = '',
  children,
  ...rest
}: BtnProps & { icon?: ReactNode; variant?: 'default' | 'accent' | 'danger' | 'pink' | 'green' }) {
  const v = variant === 'accent' ? ' ws-tool-btn-accent' : variant === 'danger' ? ' ws-tool-btn-danger' : variant === 'pink' ? ' ws-tool-btn-pink' : variant === 'green' ? ' ws-tool-btn-green' : ''
  const hint = useCompactHint(icon, children, rest)
  const { 'data-tooltip': own, ...btn } = rest as BtnProps & { 'data-tooltip'?: string }
  const h = hintsFor(own, hint, children)
  return (
    <button className={`ws-tool-btn${v} ${className}`} {...hintAttrs(btn.disabled ? h.disabled : h.shown, btn.disabled)} {...btn}>
      {icon}
      {icon && children != null && children !== false ? <span className="ws-tb-label">{children}</span> : children}
    </button>
  )
}

export function IconButton({
  icon,
  label,
  className = '',
  ...rest
}: BtnProps & { icon: ReactNode; label: string }) {
  // Taken out of the spread so it cannot land BESIDE the native `title` that
  // `hintAttrs` routes a disabled control's hint to (an element with both
  // shows two bubbles) — then honoured, because dropping a hint the caller
  // asked for is the silent kind of wrong. `label` stays the accessible name.
  const { 'data-tooltip': own, ...btn } = rest as BtnProps & { 'data-tooltip'?: string }
  return (
    <button className={`ws-icon-btn ${className}`} {...hintAttrs(own ?? label, btn.disabled)} aria-label={label} {...btn}>
      {icon}
    </button>
  )
}

export function ToolToggle({
  on,
  icon,
  className = '',
  children,
  ...rest
}: BtnProps & { on: boolean; icon?: ReactNode }) {
  const hint = useCompactHint(icon, children, rest)
  const { 'data-tooltip': own, ...btn } = rest as BtnProps & { 'data-tooltip'?: string }
  const h = hintsFor(own, hint, children)
  return (
    <button
      className={`ws-tool-btn ws-tool-toggle ${className}`}
      {...hintAttrs(btn.disabled ? h.disabled : h.shown, btn.disabled)}
      data-on={on ? '' : undefined}
      aria-pressed={on}
      {...btn}
    >
      {icon}
      {icon && children != null && children !== false ? <span className="ws-tb-label">{children}</span> : children}
    </button>
  )
}

export function IconToggle({
  on,
  icon,
  label,
  className = '',
  ...rest
}: BtnProps & { on: boolean; icon: ReactNode; label: string }) {
  // Same reason as IconButton: one hint source, and never a dropped one.
  const { 'data-tooltip': own, ...btn } = rest as BtnProps & { 'data-tooltip'?: string }
  return (
    <button
      className={`ws-icon-btn ws-tool-toggle ${className}`}
      {...hintAttrs(own ?? label, btn.disabled)}
      data-on={on ? '' : undefined}
      aria-pressed={on}
      aria-label={label}
      {...btn}
    >
      {icon}
    </button>
  )
}
